import { default as WJElement } from '../wje-element/element.js';
import styles from './styles/styles.css?inline';

/**
 * @summary Orgchart is a custom web component that extends WJElement.
 * @documentation https://elements.webjet.sk/components/Orgchart
 * @status stable
 * @augments WJElement
 * @attribute {boolean} flat - Removes the incoming connector spacing for nested charts that should visually continue on the same level.
 * @csspart - Styles the element.
 * @csspart native - Styles the native element.
 * @tag wje-orgchart
 * @example
 */
export default class Orgchart extends WJElement {
    /**
     * Creates an instance of Orgchart.
     * @class
     */
    constructor() {
        super();
    }

    /**
     * Sets whether the chart should omit its incoming connector spacing.
     * @param {boolean} value True when the chart should render without top connector spacing.
     */
    set flat(value) {
        this.toggleAttribute('flat', Boolean(value));
    }

    /**
     * Gets whether the chart omits its incoming connector spacing.
     * @returns {boolean} True when the chart has the flat attribute.
     */
    get flat() {
        return this.hasAttribute('flat');
    }

    className = 'Orgchart';

    /**
     * Returns the CSS styles for the component.
     * @static
     * @returns {CSSStyleSheet}
     */
    static get cssStyleSheet() {
        return styles;
    }

    /**
     * Returns attributes that trigger a redraw when they change.
     * @returns {Array<string>} Attribute names that require a new template render.
     */
    static get observedAttributes() {
        return ['flat'];
    }

    /**
     * Sets up the attributes for the component.
     */
    setupAttributes() {
        this.isShadowRoot = 'open';
        this.syncAria();
    }

    /**
     * Draws the component for the org chart.
     * @returns {DocumentFragment}
     */
    draw() {
        let fragment = document.createDocumentFragment();

        let native = document.createElement('div');
        native.setAttribute('part', 'native');
        native.classList.add('native-orgchart');

        let slot = document.createElement('slot');

        native.appendChild(slot);

        fragment.appendChild(native);

        this.native = native;
        this.defaultSlot = slot;

        return fragment;
    }

    /**
     * Syncs flat chart state to direct slotted children.
     */
    afterDraw() {
        this.defaultSlot.addEventListener('slotchange', this.syncFlatChildren);
        this.syncFlatChildren();
    }

    /**
     * Marks direct children that are rendered inside a flat chart.
     */
    syncFlatChildren = () => {
        let children = new Set(this.defaultSlot?.assignedElements({ flatten: true }) || []);

        this.flatChildren?.forEach((child) => {
            if (!children.has(child)) child.classList.remove('flat-child');
        });

        children.forEach((child) => child.classList.toggle('flat-child', this.flat));
        this.flatChildren = children;
    };

    /**
     * Sync ARIA attributes on host.
     */
    syncAria() {
        if (!this.hasAttribute('role')) {
            this.setAriaState({ role: 'tree' });
        }
    }
}
